import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, X, Code, FileText, Zap } from 'lucide-react';
import { apiClient } from '../../lib/api';

// Get suggested fields/templates based on condition
const getSuggestedFields = (condition, t) => {
  // Friends-related conditions
  if (condition === 'student.has_friends' || condition === 'not student.has_friends') {
    return [
      {
        label: t('quickField.friendsList', 'List all friends'),
        code: '{% for friend in student.friends %}{{friend.full_name}}{% if not forloop.last %}, {% endif %}{% endfor %}',
      },
      {
        label: t('quickField.friendsBullets', 'Friends (bullets)'),
        code: '{% for friend in student.friends %}\n- {{friend.full_name}}{% endfor %}',
      },
      {
        label: t('quickField.friendCount', 'Number of friends'),
        code: '{{student.friends.count}}',
      },
    ];
  }

  // Payment-related conditions
  if (condition.includes('balance_due') || condition.includes('total_paid') || condition.includes('deposit_paid')) {
    return [
      {
        label: t('quickField.balanceDue', 'Balance due'),
        code: '€{{balance_due}}',
      },
      {
        label: t('quickField.totalPaid', 'Total paid'),
        code: '€{{total_paid}}',
      },
      {
        label: t('quickField.depositPaid', 'Deposit paid'),
        code: '€{{deposit_paid}}',
      },
      {
        label: t('quickField.coursePrice', 'Course price'),
        code: '€{{course_price}}',
      },
    ];
  }

  // Travel-related conditions
  if (condition === 'student.travel_required' || condition === 'not student.travel_required' || condition.includes('travel_location')) {
    return [
      {
        label: t('quickField.travelLocation', 'Travel location'),
        code: '{{student.travel_location.name}}',
      },
      {
        label: t('quickField.travelAddress', 'Location address'),
        code: '{{student.travel_location.address}}',
      },
      {
        label: t('quickField.travelNotes', 'Travel notes'),
        code: '{{student.travel_notes}}',
      },
    ];
  }

  // Cinnire-related conditions
  if (condition === 'student.is_cinnire' || condition === 'not student.is_cinnire') {
    return [
      {
        label: t('quickField.studentName', 'Student name'),
        code: '{{student.full_name}}',
      },
      {
        label: t('quickField.parentName', 'Parent name'),
        code: '{{student.parent_name}}',
      },
    ];
  }

  // Course-related conditions
  if (condition.includes('student.course') || condition.includes('VALUE:student.school_year')) {
    return [
      {
        label: t('quickField.courseName', 'Course name'),
        code: '{{student.course.name}}',
      },
      {
        label: t('quickField.courseStart', 'Course start date'),
        code: '{{student.course.start_date}}',
      },
      {
        label: t('quickField.courseEnd', 'Course end date'),
        code: '{{student.course.end_date}}',
      },
      {
        label: t('quickField.schoolYear', 'School year'),
        code: '{{student.school_year}}',
      },
    ];
  }

  // Student status conditions
  if (condition.includes('student.status') || condition.includes('student.attended_before')) {
    return [
      {
        label: t('quickField.studentName', 'Student name'),
        code: '{{student.full_name}}',
      },
      {
        label: t('quickField.parentName', 'Parent name'),
        code: '{{student.parent_name}}',
      },
      {
        label: t('quickField.whenAttended', 'When attended'),
        code: '{{student.when_attended}}',
      },
    ];
  }

  // Default common fields
  return [
    {
      label: t('quickField.studentName', 'Student name'),
      code: '{{student.full_name}}',
    },
    {
      label: t('quickField.parentName', 'Parent name'),
      code: '{{student.parent_name}}',
    },
  ];
};

// Value-based conditions - allows selecting specific values
const getValueBasedConditions = (t, dynamicOptions = {}) => ({
  'student.school_year': {
    label: t('conditionBuilder.schoolYearEquals', 'School Year equals'),
    field: 'student.school_year',
    values: dynamicOptions.schoolYears || []
  },
  'student.gender': {
    label: t('conditionBuilder.genderEquals', 'Gender equals'),
    field: 'student.gender',
    values: [
      { value: 'M', label: t('gender.male', 'Male') },
      { value: 'F', label: t('gender.female', 'Female') },
    ]
  },
  'student.status': {
    label: t('conditionBuilder.statusEquals', 'Status equals'),
    field: 'student.status',
    values: [
      { value: 'ACTIVE', label: t('status.active', 'Active') },
      { value: 'WITHDRAWN', label: t('status.withdrawn', 'Withdrawn') },
      { value: 'PENDING', label: t('status.pending', 'Pending') },
    ]
  },
  'student.interview_status': {
    label: t('conditionBuilder.interviewStatusEquals', 'Interview Status equals'),
    field: 'student.interview_status',
    values: [
      { value: 'NOT_INTERVIEWED', label: t('interviewStatus.notInterviewed', 'Not Interviewed') },
      { value: 'INTERVIEWED', label: t('interviewStatus.interviewed', 'Interviewed') },
      { value: 'PASSED', label: t('interviewStatus.passed', 'Passed') },
      { value: 'FAILED', label: t('interviewStatus.failed', 'Failed') },
    ]
  },
  'student.travel_location.name': {
    label: t('conditionBuilder.travelLocationEquals', 'Travel Location equals'),
    field: 'student.travel_location.name',
    values: dynamicOptions.travelLocations || []
  },
  'student.course.name': {
    label: t('conditionBuilder.courseNameEquals', 'Course equals'),
    field: 'student.course.name',
    values: dynamicOptions.courses || []
  },
  'student.house.name': {
    label: t('conditionBuilder.houseEquals', 'House equals'),
    field: 'student.house.name',
    values: dynamicOptions.houses || []
  }
});

// Pre-built condition options
const getConditionOptions = (t) => [
  {
    category: t('conditionCategory.payment', 'Payment & Financial'),
    options: [
      { value: 'balance_due > 0', label: t('condition.balanceDue', 'Balance Due (owes money)'), code: 'balance_due > 0' },
      { value: 'balance_due <= 0', label: t('condition.fullyPaid', 'Fully Paid (no balance)'), code: 'balance_due <= 0' },
      { value: 'deposit_paid > 0', label: t('condition.depositPaid', 'Deposit Paid'), code: 'deposit_paid > 0' },
      { value: 'deposit_paid <= 0', label: t('condition.depositNotPaid', 'Deposit Not Paid'), code: 'deposit_paid <= 0' },
      { value: 'total_paid > 0', label: t('condition.hasPayments', 'Has Made Payments'), code: 'total_paid > 0' },
      { value: 'total_paid <= 0', label: t('condition.noPayments', 'No Payments Yet'), code: 'total_paid <= 0' },
    ]
  },
  {
    category: t('conditionCategory.studentStatus', 'Student Status'),
    options: [
      { value: 'student.is_cinnire', label: t('condition.isCinnire', 'Is a Cinnire (Leader)'), code: 'student.is_cinnire' },
      { value: 'not student.is_cinnire', label: t('condition.notCinnire', 'Not a Cinnire'), code: 'not student.is_cinnire' },
      { value: 'student.attended_before', label: t('condition.attendedBefore', 'Attended Before (Returning)'), code: 'student.attended_before' },
      { value: 'not student.attended_before', label: t('condition.firstTime', 'First Time Attending'), code: 'not student.attended_before' },
      { value: 'student.status == "ACTIVE"', label: t('condition.statusActive', 'Status is Active'), code: 'student.status == "ACTIVE"' },
      { value: 'student.status == "WITHDRAWN"', label: t('condition.statusWithdrawn', 'Status is Withdrawn'), code: 'student.status == "WITHDRAWN"' },
    ]
  },
  {
    category: t('conditionCategory.friends', 'Friends & Social'),
    options: [
      { value: 'student.has_friends', label: t('condition.hasFriends', 'Has Friends Attending'), code: 'student.has_friends' },
      { value: 'not student.has_friends', label: t('condition.noFriends', 'No Friends Attending'), code: 'not student.has_friends' },
    ]
  },
  {
    category: t('conditionCategory.travel', 'Travel & Logistics'),
    options: [
      { value: 'student.travel_required', label: t('condition.travelRequired', 'Travel Required'), code: 'student.travel_required' },
      { value: 'not student.travel_required', label: t('condition.noTravel', 'No Travel Required'), code: 'not student.travel_required' },
      { value: 'student.travel_location', label: t('condition.hasTravelLocation', 'Has Travel Location Set'), code: 'student.travel_location' },
    ]
  },
  {
    category: t('conditionCategory.course', 'Course Information'),
    options: [
      { value: 'student.course', label: t('condition.hasEnrolledCourse', 'Enrolled in a Course'), code: 'student.course' },
      { value: 'student.school_year', label: t('condition.hasSchoolYear', 'School Year Set'), code: 'student.school_year' },
    ]
  },
  {
    category: t('conditionCategory.specificValue', 'Specific Value Checks'),
    options: [
      { value: 'VALUE:student.school_year', label: t('conditionBuilder.schoolYearEquals', 'School Year equals'), code: '' },
      { value: 'VALUE:student.gender', label: t('conditionBuilder.genderEquals', 'Gender equals'), code: '' },
      { value: 'VALUE:student.status', label: t('conditionBuilder.statusEquals', 'Status equals'), code: '' },
      { value: 'VALUE:student.interview_status', label: t('conditionBuilder.interviewStatusEquals', 'Interview Status equals'), code: '' },
      { value: 'VALUE:student.travel_location.name', label: t('conditionBuilder.travelLocationEquals', 'Travel Location equals'), code: '' },
      { value: 'VALUE:student.course.name', label: t('conditionBuilder.courseNameEquals', 'Course equals'), code: '' },
      { value: 'VALUE:student.house.name', label: t('conditionBuilder.houseEquals', 'House equals'), code: '' },
    ]
  },
  {
    category: t('conditionCategory.custom', 'Custom Condition'),
    options: [
      { value: 'custom', label: t('condition.custom', 'Custom Condition (Advanced)'), code: '' },
    ]
  }
];

// Quick Insert Fields Component
function QuickInsertFields({ condition, onInsert, t }) {
  const suggestedFields = getSuggestedFields(condition, t);

  if (!suggestedFields || suggestedFields.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2">
      <div className="flex items-center gap-1 text-xs font-medium text-blue-900">
        <Zap className="h-3 w-3" />
        {t('conditionBuilder.quickInsert', 'Quick Insert:')}
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestedFields.map((field, idx) => (
          <Button
            key={idx}
            size="sm"
            variant="outline"
            onClick={() => onInsert(field.code)}
            className="h-7 text-xs bg-white hover:bg-blue-100 hover:border-blue-400"
          >
            {field.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function ConditionBuilder({ open, onOpenChange, onInsert }) {
  const { t } = useTranslation();
  const [ifCondition, setIfCondition] = useState('');
  const [ifContent, setIfContent] = useState('');
  const [ifCustomCode, setIfCustomCode] = useState('');
  const [ifSelectedValue, setIfSelectedValue] = useState(''); // For value-based conditions

  const [elifBlocks, setElifBlocks] = useState([]);
  const [hasElse, setHasElse] = useState(false);
  const [elseContent, setElseContent] = useState('');

  const [showPreview, setShowPreview] = useState(false);

  // Dynamic options from API
  const [travelLocations, setTravelLocations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [houses, setHouses] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);

  const conditionOptions = getConditionOptions(t);
  const valueBasedConditions = getValueBasedConditions(t, { schoolYears, travelLocations, courses, houses });

  // Refs for textareas
  const ifTextareaRef = React.useRef(null);
  const elseTextareaRef = React.useRef(null);
  const elifTextareaRefs = React.useRef({});

  // Fetch dynamic options on mount
  React.useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch school years
        try {
          const schoolYearsRes = await apiClient.get('/api/students/school-years/');
          const schoolYearsData = schoolYearsRes.data;
          // Handle both paginated and non-paginated responses
          const schoolYearsArray = Array.isArray(schoolYearsData)
            ? schoolYearsData
            : (schoolYearsData?.results || []);
          setSchoolYears(schoolYearsArray
            .filter(sy => sy.is_active)
            .sort((a, b) => a.display_order - b.display_order)
            .map(sy => ({
              value: sy.year,
              label: sy.year
            })));
        } catch (err) {
          // Fallback to common school years if API unavailable
          // Fallback to common school years
          setSchoolYears([
            { value: '1st Year', label: '1st Year' },
            { value: '2nd Year', label: '2nd Year' },
            { value: '3rd Year', label: '3rd Year' },
            { value: '4th Year', label: '4th Year' },
            { value: '5th Year', label: '5th Year' },
            { value: '6th Year', label: '6th Year' },
          ]);
        }

        // Fetch travel locations
        try {
          const locationsRes = await apiClient.get('/api/students/travel-locations/');
          const locationsData = locationsRes.data;
          // Handle both paginated and non-paginated responses
          const locationsArray = Array.isArray(locationsData)
            ? locationsData
            : (locationsData?.results || []);
          setTravelLocations(locationsArray
            .filter(loc => loc.is_active)
            .map(loc => ({
              value: loc.name,
              label: loc.name
            })));
        } catch (err) { // eslint-disable-line no-empty
        }

        // Fetch courses
        try {
          const coursesRes = await apiClient.get('/api/students/courses/');
          const coursesData = coursesRes.data;
          // Handle both paginated and non-paginated responses
          const coursesArray = Array.isArray(coursesData)
            ? coursesData
            : (coursesData?.results || []);
          setCourses(coursesArray.map(course => ({
            value: course.name,
            label: course.name
          })));
        } catch (err) { // eslint-disable-line no-empty
        }

        // Fetch houses
        try {
          const housesRes = await apiClient.get('/api/students/houses/');
          const housesData = housesRes.data;
          // Handle both paginated and non-paginated responses
          const housesArray = Array.isArray(housesData)
            ? housesData
            : (housesData?.results || []);
          setHouses(housesArray.map(house => ({
            value: house.name,
            label: house.name
          })));
        } catch (err) { // eslint-disable-line no-empty
        }
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
      }
    };

    if (open) {
      fetchOptions();
    }
  }, [open]);

  // Insert field at cursor or end
  const insertIntoTextarea = (textareaRef, currentValue, setValueFn, code) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = currentValue.substring(0, start) + code + currentValue.substring(end);
      setValueFn(newValue);

      // Restore focus and cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + code.length, start + code.length);
      }, 0);
    } else {
      // Fallback: append to end
      setValueFn(currentValue + (currentValue ? ' ' : '') + code);
    }
  };

  // Reset form when dialog closes
  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const resetForm = () => {
    setIfCondition('');
    setIfContent('');
    setIfCustomCode('');
    setIfSelectedValue('');
    setElifBlocks([]);
    setHasElse(false);
    setElseContent('');
    setShowPreview(false);
  };

  // Add new elif block
  const addElifBlock = () => {
    setElifBlocks([...elifBlocks, { id: Date.now(), condition: '', content: '', customCode: '', selectedValue: '' }]);
  };

  // Remove elif block
  const removeElifBlock = (id) => {
    setElifBlocks(elifBlocks.filter(block => block.id !== id));
  };

  // Update elif block
  const updateElifBlock = (id, field, value) => {
    setElifBlocks(elifBlocks.map(block =>
      block.id === id ? { ...block, [field]: value } : block
    ));
  };

  // Helper to check if condition is value-based
  const isValueBasedCondition = (conditionValue) => {
    return conditionValue && conditionValue.startsWith('VALUE:');
  };

  // Generate code for a condition (handles value-based and regular conditions)
  const generateConditionCode = (conditionValue, selectedValue, customCode) => {
    if (conditionValue === 'custom') {
      return customCode;
    }
    if (isValueBasedCondition(conditionValue)) {
      const fieldKey = conditionValue.replace('VALUE:', '');
      if (selectedValue) {
        return `${fieldKey} == "${selectedValue}"`;
      }
      return '';
    }
    return conditionValue;
  };

  // Generate Django template code
  const generateTemplate = () => {
    let template = '';

    // IF block
    const ifCode = generateConditionCode(ifCondition, ifSelectedValue, ifCustomCode);
    if (!ifCode || !ifContent.trim()) return '';

    template += `{% if ${ifCode} %}\n${ifContent.trim()}\n`;

    // ELIF blocks
    elifBlocks.forEach(block => {
      const elifCode = generateConditionCode(block.condition, block.selectedValue, block.customCode);
      if (elifCode && block.content.trim()) {
        template += `{% elif ${elifCode} %}\n${block.content.trim()}\n`;
      }
    });

    // ELSE block
    if (hasElse && elseContent.trim()) {
      template += `{% else %}\n${elseContent.trim()}\n`;
    }

    template += '{% endif %}';

    return template;
  };

  // Handle insert
  const handleInsert = () => {
    const template = generateTemplate();
    if (template) {
      onInsert(template);
      handleOpenChange(false);
    }
  };

  // Check if form is valid
  const isValid = () => {
    const ifCode = generateConditionCode(ifCondition, ifSelectedValue, ifCustomCode);
    return ifCode && ifContent.trim();
  };

  const template = generateTemplate();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            {t('conditionBuilder.title', 'Conditional Content Builder')}
          </DialogTitle>
          <DialogDescription>
            {t('conditionBuilder.description', 'Create smart messages that show different content based on conditions. Perfect for personalized emails.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* IF Block */}
          <div className="border-l-4 border-blue-500 pl-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-blue-700 text-sm">IF</span>
              <span className="text-xs text-gray-500">{t('conditionBuilder.required', '(Required)')}</span>
            </div>

            <div className="space-y-2">
              <Label>{t('conditionBuilder.selectCondition', 'Select Condition')}</Label>
              <Select value={ifCondition} onValueChange={setIfCondition}>
                <SelectTrigger>
                  <SelectValue placeholder={t('conditionBuilder.chooseCondition', 'Choose a condition...')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {conditionOptions.map((category) => (
                    <React.Fragment key={category.category}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                        {category.category}
                      </div>
                      {category.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {ifCondition === 'custom' && (
              <div className="space-y-2">
                <Label>{t('conditionBuilder.customCode', 'Custom Condition Code')}</Label>
                <Textarea
                  value={ifCustomCode}
                  onChange={(e) => setIfCustomCode(e.target.value)}
                  placeholder="e.g., student.age >= 16"
                  className="font-mono text-sm"
                  rows={2}
                />
                <p className="text-xs text-gray-500">
                  {t('conditionBuilder.customHelp', 'Enter Django template condition syntax')}
                </p>
              </div>
            )}

            {isValueBasedCondition(ifCondition) && (
              <div className="space-y-2">
                <Label>{t('conditionBuilder.selectValue', 'Select Value')}</Label>
                <Select value={ifSelectedValue} onValueChange={setIfSelectedValue}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('conditionBuilder.chooseValue', 'Choose a value...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {valueBasedConditions[ifCondition.replace('VALUE:', '')]?.values?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('conditionBuilder.thenShow', 'Then Show This Message')}</Label>
              <Textarea
                ref={ifTextareaRef}
                value={ifContent}
                onChange={(e) => setIfContent(e.target.value)}
                placeholder={t('conditionBuilder.messageExample', 'Enter the message to show when this condition is true...')}
                rows={4}
              />
              <QuickInsertFields
                condition={generateConditionCode(ifCondition, ifSelectedValue, ifCustomCode)}
                onInsert={(code) => insertIntoTextarea(ifTextareaRef, ifContent, setIfContent, code)}
                t={t}
              />
            </div>
          </div>

          {/* ELIF Blocks */}
          {elifBlocks.map((block, index) => (
            <div key={block.id} className="border-l-4 border-amber-500 pl-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-amber-700 text-sm">ELSE IF</span>
                  <span className="text-xs text-gray-500">#{index + 1}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeElifBlock(block.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label>{t('conditionBuilder.selectCondition', 'Select Condition')}</Label>
                <Select value={block.condition} onValueChange={(val) => updateElifBlock(block.id, 'condition', val)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('conditionBuilder.chooseCondition', 'Choose a condition...')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {conditionOptions.map((category) => (
                      <React.Fragment key={category.category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                          {category.category}
                        </div>
                        {category.options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {block.condition === 'custom' && (
                <div className="space-y-2">
                  <Label>{t('conditionBuilder.customCode', 'Custom Condition Code')}</Label>
                  <Textarea
                    value={block.customCode}
                    onChange={(e) => updateElifBlock(block.id, 'customCode', e.target.value)}
                    placeholder="e.g., student.age >= 16"
                    className="font-mono text-sm"
                    rows={2}
                  />
                </div>
              )}

              {isValueBasedCondition(block.condition) && (
                <div className="space-y-2">
                  <Label>{t('conditionBuilder.selectValue', 'Select Value')}</Label>
                  <Select value={block.selectedValue || ''} onValueChange={(val) => updateElifBlock(block.id, 'selectedValue', val)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('conditionBuilder.chooseValue', 'Choose a value...')} />
                    </SelectTrigger>
                    <SelectContent>
                      {valueBasedConditions[block.condition.replace('VALUE:', '')]?.values.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('conditionBuilder.thenShow', 'Then Show This Message')}</Label>
                <Textarea
                  ref={(el) => { if (el) elifTextareaRefs.current[block.id] = { current: el }; }}
                  value={block.content}
                  onChange={(e) => updateElifBlock(block.id, 'content', e.target.value)}
                  placeholder={t('conditionBuilder.messageExample', 'Enter the message to show when this condition is true...')}
                  rows={4}
                />
                <QuickInsertFields
                  condition={generateConditionCode(block.condition, block.selectedValue, block.customCode)}
                  onInsert={(code) => {
                    const ref = elifTextareaRefs.current[block.id];
                    insertIntoTextarea(ref, block.content, (val) => updateElifBlock(block.id, 'content', val), code);
                  }}
                  t={t}
                />
              </div>
            </div>
          ))}

          {/* Add ELIF Button */}
          <Button
            variant="outline"
            onClick={addElifBlock}
            className="w-full border-dashed border-2 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('conditionBuilder.addElseIf', 'Add Else If')}
          </Button>

          {/* ELSE Block Toggle */}
          {!hasElse && (
            <Button
              variant="outline"
              onClick={() => setHasElse(true)}
              className="w-full border-dashed border-2 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('conditionBuilder.addElse', 'Add Else (Optional Fallback)')}
            </Button>
          )}

          {/* ELSE Block */}
          {hasElse && (
            <div className="border-l-4 border-gray-500 pl-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-700 text-sm">ELSE</span>
                  <span className="text-xs text-gray-500">{t('conditionBuilder.otherwiseShow', '(Otherwise show)')}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setHasElse(false); setElseContent(''); }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label>{t('conditionBuilder.defaultMessage', 'Default Message')}</Label>
                <Textarea
                  ref={elseTextareaRef}
                  value={elseContent}
                  onChange={(e) => setElseContent(e.target.value)}
                  placeholder={t('conditionBuilder.elseExample', 'Enter the message to show if none of the above conditions are true...')}
                  rows={4}
                />
                <QuickInsertFields
                  condition={generateConditionCode(ifCondition, ifSelectedValue, ifCustomCode)}
                  onInsert={(code) => insertIntoTextarea(elseTextareaRef, elseContent, setElseContent, code)}
                  t={t}
                />
              </div>
            </div>
          )}

          {/* Preview Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="w-full"
            disabled={!isValid()}
          >
            <Code className="h-4 w-4 mr-2" />
            {showPreview ? t('conditionBuilder.hidePreview', 'Hide Preview') : t('conditionBuilder.showPreview', 'Show Code Preview')}
          </Button>

          {/* Code Preview */}
          {showPreview && template && (
            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
              <pre className="whitespace-pre-wrap">{template}</pre>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleInsert}
            disabled={!isValid()}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <FileText className="h-4 w-4 mr-2" />
            {t('conditionBuilder.insertConditional', 'Insert Conditional Content')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
